/**
 * Checkout API Handler for React Application
 * Updated for PHP Backend Compatibility
 */
import { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

/**
 * Get authentication token
 */
const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Get headers for API requests
 */
const getHeaders = (includeAuth = false) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  let data;
  
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
  }

  if (!response.ok) {
    throw {
      status: response.status,
      message: data.message || 'An error occurred',
      errors: data.errors || [],
      data: data
    };
  }

  return data;
};

/**
 * Checkout API Class
 */
class CheckoutAPI {
  /**
   * Create new order
   */
  static async createOrder(orderData) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: getHeaders(!!token), // Include auth if token exists
        body: JSON.stringify(orderData)
      });

      const result = await handleResponse(response);
      
      // PHP returns: { success: true, data: { order }, message }
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Create order error:', error);
      throw error;
    }
  }

  /**
   * Get order by order number
   */
  static async getOrder(orderNumber) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderNumber}`, {
        method: 'GET',
        headers: getHeaders(false)
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Get order error:', error);
      throw error;
    }
  }

  /**
   * Get orders by customer email
   */
  static async getCustomerOrders(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/customer/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: getHeaders(false)
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Get customer orders error:', error);
      throw error;
    }
  }

  /**
   * Get authenticated user's orders
   */
  static async getMyOrders() {
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }
      
      console.log('Fetching my orders with token:', token ? 'Token exists' : 'No token');
      
      const response = await fetch(`${API_BASE_URL}/orders/my-orders`, {
        method: 'GET',
        headers: getHeaders(true) // Include auth token
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Get my orders error:', error);
      throw error;
    }
  }

  /**
   * List all orders (Admin only)
   */
  static async listOrders(filters = {}) {
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }
      
      const queryParams = new URLSearchParams();
      
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.per_page) queryParams.append('per_page', filters.per_page);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);
      
      const url = `${API_BASE_URL}/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(true)
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('List orders error:', error);
      throw error;
    }
  }

  /**
   * Update order status (Admin only)
   */
  static async updateOrderStatus(orderId, status, notes = '') {
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }
      
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify({ status, notes })
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Update order status error:', error);
      throw error;
    }
  }

  /**
   * Cancel order
   */
  static async cancelOrder(orderId, reason = 'Cancelled by customer') {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify({ reason })
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Cancel order error:', error);
      throw error;
    }
  }

  /**
   * Assign guest order to authenticated user
   */
  static async assignOrderToUser(orderId) {
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }
      
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/assign-to-user`, {
        method: 'POST',
        headers: getHeaders(true)
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Assign order error:', error);
      throw error;
    }
  }

  /**
   * Get order statistics (Admin only)
   */
  static async getOrderStats() {
    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }
      
      const response = await fetch(`${API_BASE_URL}/orders/stats`, {
        method: 'GET',
        headers: getHeaders(true)
      });

      const result = await handleResponse(response);
      
      return {
        success: result.success,
        message: result.message,
        data: result.data || result
      };
    } catch (error) {
      console.error('Get order stats error:', error);
      throw error;
    }
  }

  /**
   * Validate order data before submission
   */
  static validateOrderData(orderData) {
    const errors = [];

    // Customer info validation
    if (!orderData.customerInfo) {
      errors.push('Customer information is required');
    } else {
      if (!orderData.customerInfo.fullName?.trim()) {
        errors.push('Full name is required');
      }
      if (!orderData.customerInfo.email?.trim()) {
        errors.push('Email is required');
      } else if (!this.isValidEmail(orderData.customerInfo.email)) {
        errors.push('Invalid email address');
      }
      if (!orderData.customerInfo.phone?.trim()) {
        errors.push('Phone number is required');
      }
    }

    // Shipping address validation
    if (!orderData.shippingAddress) {
      errors.push('Shipping address is required');
    } else {
      if (!orderData.shippingAddress.address?.trim()) {
        errors.push('Street address is required');
      }
      if (!orderData.shippingAddress.city?.trim()) {
        errors.push('City is required');
      }
      if (!orderData.shippingAddress.state?.trim()) {
        errors.push('State/Province is required');
      }
      if (!orderData.shippingAddress.zipCode?.trim()) {
        errors.push('Zip/Postal code is required');
      }
    }

    // Items validation
    if (!orderData.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
      errors.push('At least one item is required');
    } else {
      orderData.items.forEach((item, index) => {
        if (!item.id) {
          errors.push(`Item ${index + 1}: ID is required`);
        }
        if (!item.price || item.price <= 0) {
          errors.push(`Item ${index + 1}: Valid price is required`);
        }
        if (!item.quantity || item.quantity <= 0) {
          errors.push(`Item ${index + 1}: Valid quantity is required`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email format
   */
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Format cart data for order submission
   */
  static formatOrderData(cart, customerInfo, shippingAddress, options = {}) {
    return {
      customerInfo: {
        fullName: customerInfo.fullName?.trim(),
        email: customerInfo.email?.trim(),
        phone: customerInfo.phone?.trim(),
        company: customerInfo.company?.trim() || null
      },
      shippingAddress: {
        address: shippingAddress.address?.trim(),
        city: shippingAddress.city?.trim(),
        state: shippingAddress.state?.trim(),
        zipCode: shippingAddress.zipCode?.trim(),
        country: shippingAddress.country?.trim() || 'Pakistan'
      },
      items: cart.map(item => ({
        id: item.id,
        type: item.type || 'product',
        title: item.name || item.title,
        sku: item.sku || `${item.type}-${item.id}`,
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity),
        category: item.category?.name || item.category,
        image: item.image || item.primaryImage || item.primary_image,
        isOnSale: item.isOnSale || item.is_on_sale || false,
        originalPrice: item.originalPrice || item.original_price || null
      })),
      deliveryTimePreference: options.deliveryTimePreference || 'anytime',
      specialInstructions: options.specialInstructions?.trim() || null,
      orderNotes: options.orderNotes?.trim() || null
    };
  }

  /**
   * Calculate order totals
   */
  static calculateTotals(items) {
    const subtotal = items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);

    const tax = 0; // Can be calculated based on your tax rules
    const shipping = 0; // Can be calculated based on shipping rules
    const discount = 0;
    const total = subtotal + tax + shipping - discount;

    return {
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      shipping: shipping.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2)
    };
  }

  /**
   * Format price for display (Pakistani Rupee)
   */
static formatPrice(price) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}


  /**
   * Get order status badge color
   */
  static getStatusColor(status) {
    const colors = {
      'pending': '#ffc107',
      'confirmed': '#17a2b8',
      'processing': '#007bff',
      'shipped': '#6f42c1',
      'delivered': '#28a745',
      'cancelled': '#dc3545'
    };
    return colors[status] || '#6c757d';
  }

  /**
   * Get order status display text
   */
  static getStatusText(status) {
    const texts = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'processing': 'Processing',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return texts[status] || status;
  }
}

/**
 * React Hook for Checkout
 */
export const useCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  const placeOrder = async (orderData) => {
    setLoading(true);
    setError(null);

    try {
      // Validate order data
      const validation = CheckoutAPI.validateOrderData(orderData);
      if (!validation.isValid) {
        throw {
          message: 'Validation failed',
          errors: validation.errors
        };
      }

      // Create order
      const response = await CheckoutAPI.createOrder(orderData);

      if (response.success) {
        setOrder(response.data.order);
        return response.data.order;
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (err) {
      const errorMessage = err.errors && err.errors.length > 0
        ? err.errors.join(', ')
        : err.message || 'An error occurred while placing the order';
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const trackOrder = async (orderNumber) => {
    setLoading(true);
    setError(null);

    try {
      const response = await CheckoutAPI.getOrder(orderNumber);

      if (response.success) {
        setOrder(response.data.order);
        return response.data.order;
      } else {
        throw new Error(response.message || 'Failed to fetch order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while tracking the order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getMyOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await CheckoutAPI.getMyOrders();

      if (response.success) {
        return response.data.orders || [];
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching orders');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await CheckoutAPI.cancelOrder(orderId, reason);

      if (response.success) {
        setOrder(response.data.order);
        return response.data.order;
      } else {
        throw new Error(response.message || 'Failed to cancel order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while cancelling the order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const listOrders = async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await CheckoutAPI.listOrders(filters);

      if (response.success) {
        return {
          orders: response.data.orders || [],
          pagination: response.data.pagination || {}
        };
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching orders');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status, notes = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await CheckoutAPI.updateOrderStatus(orderId, status, notes);

      if (response.success) {
        setOrder(response.data.order);
        return response.data.order;
      } else {
        throw new Error(response.message || 'Failed to update order status');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating order status');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    order,
    placeOrder,
    trackOrder,
    getMyOrders,
    cancelOrder,
    listOrders,
    updateOrderStatus
  };
};

export default CheckoutAPI;